import { useEffect, useState } from "react"
import Checkbox from "@/v2-ui/checkbox"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import { useDebouncedValue } from "@/v2-ui/utils/utils.debounce"
import Button from "@/v2-backoffice-client/components/button"
import {
  FEATURE_CODE_PATTERN,
  FEATURE_TYPE_CODE,
} from "@/v2-backoffice-client/feature/feature.constants"
import { useFeatureRenameImpactQuery } from "@/v2-backoffice-client/feature/rename/feature.rename.impact.query"
import { useFeatureRenameMutation } from "@/v2-backoffice-client/feature/rename/feature.rename.mutation"

export type FeatureRenameDialogProps = DialogProps & {
  featureCode: string
  /** The feature's display name, for the title; falls back to the code. */
  name?: string | null
  /** Fired with the new code after a successful rename. */
  onRenamed?(newCode: string): void
}

function countLabel(count: number, singular: string, plural?: string) {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`
}

/**
 * Renames a feature code, carrying every reference with it — in the running
 * environment immediately, and in the authoring catalog so the next publish
 * keeps it (the backend refuses a rename that cannot reach both).
 *
 * The impact summary is not decoration: favourites and saved custom-field
 * values are USER data hanging off the code, and the operator should see
 * what moves before moving it. A UI-component code gets a hard warning
 * instead of a summary — its literals are compiled into the app, where no
 * database operation can follow.
 */
function FeatureRenameDialog(props: FeatureRenameDialogProps) {
  const { featureCode, name, onRenamed, open, onOpenChange, ...dialogProps } =
    props

  const [newCode, setNewCode] = useState("")
  const [isFrontendAcknowledged, setFrontendAcknowledged] = useState(false)

  // Re-arm for each opening.
  useEffect(() => {
    if (!open) return
    setNewCode("")
    setFrontendAcknowledged(false)
  }, [open])

  const trimmed = newCode.trim()
  const isCodeShapeValid = trimmed === "" || FEATURE_CODE_PATTERN.test(trimmed)

  const debounced = useDebouncedValue(trimmed, 350)
  const impactQuery = useFeatureRenameImpactQuery({
    skip: !open,
    variables: {
      featureCode,
      newFeatureCode: debounced === "" ? null : debounced,
    },
  })
  const impact = impactQuery.data?.getFeatureRenameImpact

  // The availability verdict lags the keystrokes by the debounce, so the
  // submit stays disabled until the check has caught up with the field.
  const isSettled = debounced === trimmed && !impactQuery.loading
  const isTaken = isSettled && trimmed !== "" && (impact?.NewCodeInUse ?? false)

  const isUiComponent =
    impact?.FeatureTypeCode === FEATURE_TYPE_CODE.uiComponent

  const carried = impact
    ? [
        impact.SublayerCount > 0 &&
          countLabel(impact.SublayerCount, "sublayer"),
        impact.CategoryLinkCount > 0 &&
          countLabel(impact.CategoryLinkCount, "category link"),
        impact.CategorizedLayerCount > 0 &&
          countLabel(
            impact.CategorizedLayerCount,
            "layer in this category",
            "layers in this category",
          ),
        impact.FavoriteCount > 0 &&
          `${countLabel(impact.FavoriteCount, "user favourite")} (user data)`,
        impact.ObjectValueCount > 0 &&
          `${countLabel(impact.ObjectValueCount, "saved custom-field value")} (user data)`,
        impact.CountryCount > 0 &&
          countLabel(impact.CountryCount, "country row"),
      ].filter((part): part is string => Boolean(part))
    : []

  const [renameFeature, renameState] = useFeatureRenameMutation({
    // A code is identity across catalogs, availability lists and sheets, so
    // every table on screen may hold the old one: refetch what is mounted
    // rather than guessing at query names from here.
    refetchQueries: "active",
  })

  const canSubmit =
    trimmed !== "" &&
    trimmed !== featureCode &&
    isCodeShapeValid &&
    isSettled &&
    !isTaken &&
    (!isUiComponent || isFrontendAcknowledged)

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content className="w-11/12 max-w-screen-sm" align="top">
        <Dialog.Header>
          <Dialog.Title>Rename {name ?? featureCode}</Dialog.Title>
          <Dialog.Description>
            The code is the feature&apos;s permanent identifier — its text,
            availability, favourites and saved values all hang off it. Renaming
            carries every reference along: the running environment changes
            immediately, and the authoring catalog moves with it so the next
            publish keeps the rename. Both must succeed or nothing changes.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-2 text-center" error={renameState.error} />

        <form
          id="feature-rename-form"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (!canSubmit) return
            void renameFeature({
              variables: {
                input: { FeatureCode: featureCode, NewFeatureCode: trimmed },
              },
            }).then((result) => {
              const renamed = result.data?.renameFeature
              if (!renamed) return
              onOpenChange?.(false)
              toast({
                title: `${featureCode} is now ${renamed.Code}.`,
                description:
                  "Renamed in the runtime and the authoring catalog — favourites and saved values moved with it.",
              })
              onRenamed?.(renamed.Code)
            })
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="feature-rename-code">New feature code</Label>
            <Input
              id="feature-rename-code"
              required
              className={cn(
                "font-mono text-xs",
                (!isCodeShapeValid || isTaken) && "border-destructive",
              )}
              placeholder={featureCode}
              value={newCode}
              onChange={(event) => setNewCode(event.target.value)}
            />
            <p
              className={cn(
                "text-[11px]",
                !isCodeShapeValid || isTaken
                  ? "text-destructive"
                  : "text-muted-foreground",
              )}
            >
              {!isCodeShapeValid
                ? "Letters, digits and underscores only, starting with a letter."
                : isTaken
                  ? `${trimmed} is already in use.`
                  : `Replaces ${featureCode}.`}
            </p>
          </div>

          {impact ? (
            isUiComponent ? (
              <div className="flex flex-col gap-2 rounded-md border border-destructive/50 p-3 text-xs">
                <p className="font-medium text-destructive">
                  This is a UI component code — the app compiles it in as a
                  literal, where no database rename can follow.
                </p>
                <p className="text-muted-foreground">
                  Until a repo change renames the literal too: the console falls
                  back to its bundled English for this text, tracking keeps
                  emitting the old code, and the nightly text sync resurfaces
                  the old code in the promotion queue while the new one shows up
                  as missing a code text.
                </p>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={isFrontendAcknowledged}
                    onCheckedChange={(checked) =>
                      setFrontendAcknowledged(checked === true)
                    }
                    data-feature-code="UI_BackofficeFeatures_RenameDialog_Acknowledge"
                  />
                  <span>
                    I understand the frontend literal must be renamed in a
                    separate code change.
                  </span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {impact.FeatureTypeName}
                {carried.length > 0
                  ? ` — moves with it: ${carried.join(", ")}.`
                  : " — nothing references this code beyond the feature row itself."}
              </p>
            )
          ) : null}
        </form>

        <Dialog.Footer className="mt-4 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            data-feature-code="UI_BackofficeFeatures_RenameDialog_Cancel"
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="feature-rename-form"
            iconSrc="pencil"
            disabled={!canSubmit}
            isLoading={renameState.loading}
            data-feature-code="UI_BackofficeFeatures_RenameDialog_Submit"
          >
            Rename
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default FeatureRenameDialog
